import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt);

export const exportToExcel = (data, columns, filename) => {
  const rows = data.map(row => {
    const obj = {};
    columns.forEach(col => {
      obj[col.header] = col.format ? col.format(row[col.key], row) : row[col.key];
    });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  const colWidths = columns.map(col => ({ wch: Math.max(col.header.length, 15) }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportToPDF = (data, columns, title, filename) => {
  const doc = new jsPDF('l', 'mm', 'a4');
  doc.setFontSize(18);
  doc.setTextColor(40);
  doc.text(title, 14, 20);
  doc.setFontSize(10);
  doc.setTextColor(128);
  doc.text(`Myzow Solutions LLP — Generated on ${new Date().toLocaleDateString('en-IN')}`, 14, 28);

  const head = [columns.map(c => c.header)];
  const body = data.map(row => columns.map(col => col.format ? col.format(row[col.key], row) : String(row[col.key] ?? '')));

  autoTable(doc, {
    head,
    body,
    startY: 34,
    theme: 'striped',
    headStyles: { fillColor: [255, 69, 0], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 3 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  doc.save(`${filename}.pdf`);
};

export const generateInvoicePDF = (invoice) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();

  // Header bar
  doc.setFillColor(255, 69, 0);
  doc.rect(0, 0, pageW, 40, 'F');
  doc.setTextColor(255);
  doc.setFontSize(24);
  doc.setFont(undefined, 'bold');
  doc.text('INVOICE', 20, 26);
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  doc.text(invoice.invoice_number || 'INV-XXXX', pageW - 20, 26, { align: 'right' });

  // Company info
  doc.setTextColor(60);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Myzow Solutions LLP', 20, 56);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text('Pune, Maharashtra, India', 20, 63);
  doc.text('GST: 27AABFM1234A1Z5', 20, 69);
  doc.text('info@myzow.com | +91 98765 43210', 20, 75);

  // Invoice details (right side)
  doc.setTextColor(80);
  doc.setFontSize(10);
  const rightX = pageW - 20;
  doc.text(`Issued: ${invoice.issued_date || new Date().toLocaleDateString('en-IN')}`, rightX, 56, { align: 'right' });
  doc.text(`Due: ${invoice.due_date || '-'}`, rightX, 63, { align: 'right' });
  const statusText = (invoice.status || 'draft').toUpperCase();
  doc.setFont(undefined, 'bold');
  doc.setTextColor(invoice.status === 'paid' ? 16 : invoice.status === 'overdue' ? 239 : 100, invoice.status === 'paid' ? 185 : invoice.status === 'overdue' ? 68 : 100, invoice.status === 'paid' ? 129 : invoice.status === 'overdue' ? 68 : 100);
  doc.text(statusText, rightX, 70, { align: 'right' });

  // Divider
  doc.setDrawColor(230);
  doc.line(20, 82, pageW - 20, 82);

  // Bill To
  doc.setTextColor(60);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.text('BILL TO', 20, 92);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(11);
  doc.setTextColor(40);
  doc.text(invoice.client_name || 'Client', 20, 100);

  // Items table
  const items = invoice.items || [
    { description: 'Development Services', qty: 1, rate: invoice.subtotal || invoice.amount || 0, total: invoice.subtotal || invoice.amount || 0 },
  ];

  autoTable(doc, {
    startY: 112,
    head: [['#', 'Description', 'Qty', 'Rate (INR)', 'Total (INR)']],
    body: items.map((item, i) => [
      i + 1,
      item.description,
      item.qty,
      formatCurrency(item.rate),
      formatCurrency(item.total || item.rate * item.qty),
    ]),
    theme: 'plain',
    headStyles: { fillColor: [245, 245, 245], textColor: [60, 60, 60], fontStyle: 'bold', fontSize: 9 },
    styles: { fontSize: 10, cellPadding: 5 },
    columnStyles: { 0: { cellWidth: 12 }, 3: { halign: 'right' }, 4: { halign: 'right' } },
  });

  const finalY = doc.lastAutoTable.finalY + 10;

  // Totals
  const totalsX = pageW - 80;
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text('Subtotal:', totalsX, finalY);
  doc.text(formatCurrency(invoice.subtotal || invoice.amount || 0), pageW - 20, finalY, { align: 'right' });

  doc.text('Tax (GST 18%):', totalsX, finalY + 8);
  doc.text(formatCurrency(invoice.tax_amount || invoice.tax || 0), pageW - 20, finalY + 8, { align: 'right' });

  doc.setDrawColor(255, 69, 0);
  doc.setLineWidth(0.5);
  doc.line(totalsX, finalY + 13, pageW - 20, finalY + 13);

  doc.setFontSize(13);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 69, 0);
  doc.text('TOTAL:', totalsX, finalY + 22);
  doc.text(formatCurrency(invoice.total || 0), pageW - 20, finalY + 22, { align: 'right' });

  // Footer
  doc.setDrawColor(230);
  doc.line(20, 270, pageW - 20, 270);
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.setFont(undefined, 'normal');
  doc.text('Thank you for your business! Payment terms: Net 30 days.', pageW / 2, 278, { align: 'center' });
  doc.text('Myzow Solutions LLP | www.myzow.com', pageW / 2, 284, { align: 'center' });

  doc.save(`${invoice.invoice_number || 'invoice'}.pdf`);
};
